import React, { useMemo } from "react";
import { EventType } from "../../../../../api/events";
import {
  AssessmentUI,
  FraudAngleRecommendation,
  IOAppointingAuthority,
} from "../../../../../api/observationByAuthority";
import { useAppSelector } from "../../../../../app/hooks";
import { selectEventByTypeAndEntityId } from "../../../../timeline/logic/slice";
import ReportFormTable, {
  DateInputCell,
  RadioGroupInputCell,
  RadioOption,
  ReportFormRow,
  TextAreaInputCell,
} from "../../common/ReportTableForm";

export const RECOMMENDATION_OPTIONS: RadioOption<FraudAngleRecommendation>[] = [
  {
    label: "Fraud",
    value: FraudAngleRecommendation.FRAUD,
    selectedBorderClass: "border-red-600",
    selectedBgClass: "bg-red-50",
    selectedDotClass: "bg-red-600",
  },
  {
    label: "No Fraud",
    value: FraudAngleRecommendation.NO_FRAUD,
    selectedBorderClass: "border-[#16A34A]",
    selectedBgClass: "bg-[#E8F6ED]",
    selectedDotClass: "bg-[#16A34A]",
  },
  {
    label: "Attempted Fraud",
    value: FraudAngleRecommendation.ATTEMPTED_FRAUD,
    selectedBorderClass: "border-yellow-600",
    selectedBgClass: "bg-yellow-50",
    selectedDotClass: "bg-yellow-600",
  },
];

const DATE_LABELS: Record<IOAppointingAuthority, string> = {
  [IOAppointingAuthority.ZFRC]: "Date of ZFRC",
  [IOAppointingAuthority.ZONAL_INSPECTORATE]: "Date of ZI Recommendation",
  [IOAppointingAuthority.HEAD_OF_INTERNAL_AUDIT]: "Date of HIA Recommendation",
};

const EVENT_TYPES: Record<IOAppointingAuthority, EventType> = {
  [IOAppointingAuthority.ZFRC]: EventType.ZFRC_MEETING_HELD,
  [IOAppointingAuthority.ZONAL_INSPECTORATE]: EventType.ZI_RECOMMENDATIONS,
  [IOAppointingAuthority.HEAD_OF_INTERNAL_AUDIT]: EventType.HIA_RECOMMENDATIONS,
};

interface ObservationsFormProps {
  assessment: AssessmentUI;
  isSaved: boolean;
  onFieldChange: <K extends keyof AssessmentUI>(key: K, value: AssessmentUI[K]) => void;
}

const ObservationsForm: React.FC<ObservationsFormProps> = ({ assessment, isSaved, onFieldChange }) => {
  const { competent_authority, assessment_id } = assessment;

  const isNewRow = !isSaved;

  const eventType = competent_authority ? EVENT_TYPES[competent_authority] : undefined;

  const assessmentEvent = useAppSelector(
    assessment_id && eventType ? selectEventByTypeAndEntityId(eventType, assessment_id) : () => undefined,
  );

  const eventDate = assessmentEvent?.date;

  const rows = useMemo((): ReportFormRow[] => {
    if (!competent_authority) return [];

    const dateLabel = DATE_LABELS[competent_authority] ?? "Date of Recommendation";

    const dateRow: ReportFormRow = {
      label: dateLabel,
      inputCell: (
        <DateInputCell
          value={isNewRow ? assessment.assessment_date : eventDate}
          onChange={(val) => onFieldChange("assessment_date", val)}
          disabled={!isNewRow}
        />
      ),
    };

    const recommendationRow: ReportFormRow = {
      label: "Recommendation",
      inputCell: (
        <RadioGroupInputCell
          value={assessment.recommendation}
          onChange={(val) => onFieldChange("recommendation", val as FraudAngleRecommendation)}
          options={RECOMMENDATION_OPTIONS}
        />
      ),
    };

    switch (competent_authority) {
      case IOAppointingAuthority.ZFRC:
        return [
          dateRow,
          {
            label: "Minutes of ZFRC",
            inputCell: (
              <TextAreaInputCell
                value={assessment.meeting_minutes ?? ""}
                onChange={(val) => onFieldChange("meeting_minutes", val)}
                placeholder="Enter minutes of the meeting"
                disableResize
                rows={4}
                textAreaClassName="min-h-[128px]"
              />
            ),
          },
          {
            label: "Deliberation from Convener",
            inputCell: (
              <TextAreaInputCell
                value={assessment.deliberation_from_convener ?? ""}
                onChange={(val) => onFieldChange("deliberation_from_convener", val)}
                placeholder="Enter deliberation from convener"
                disableResize
                rows={4}
                textAreaClassName="min-h-[128px]"
              />
            ),
          },
          recommendationRow,
        ];

      case IOAppointingAuthority.ZONAL_INSPECTORATE:
      case IOAppointingAuthority.HEAD_OF_INTERNAL_AUDIT:
        return [dateRow, recommendationRow];

      default:
        return [dateRow, recommendationRow];
    }
  }, [assessment, isNewRow, onFieldChange, competent_authority]);

  if (!competent_authority) {
    return null;
  }

  return (
    <div className="flex flex-col mb-7">
      <span className="font-sans font-semibold text-sm leading-5 tracking-[0%] text-[#737373] my-1.5">
        1. Observations
      </span>
      <ReportFormTable rows={rows} />
    </div>
  );
};

export default ObservationsForm;
